import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TriangleAlert } from 'lucide-react'
import { GameType } from '@/modules/promotion/type'
import { useDateFilter } from '@/common/hooks/useDateFilter'

import { Button } from '@/common/ui/button'
import { RtpConfirmDialog } from '@/modules/promotion/components/RtpConfirmDialog'
import {
	ReturnLossFormValues,
	returnLossFormSchema,
} from './components/FormSchema'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { RadioGroup, RadioGroupItem } from '@/common/ui/radio-group'
import { DateRangePicker } from '@/common/components/DateRangePicker'
import { GAME_TYPES, GameTypeCreditSettings } from '../GameTypeCreditSettings'
import { CALCULATE_INTERVALS } from './components/FormSchema'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/common/ui/select'
import { NameField } from '../CommonField'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import { Input } from '@/common/ui/input'
import {
	WalletConditionSection,
	prepareWalletConditionForSubmit,
} from '@/common/components/WalletConditionSection'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'
import { showFormValidationToast } from '@/common/utils/showFormValidationToast'

interface ReturnLossFormProps {
	defaultValues?: Partial<ReturnLossFormValues>
	onSubmit: (data: any) => void
	mode: 'create' | 'edit'
}

export default function ReturnLossForm({
	defaultValues,
	onSubmit,
	mode,
}: ReturnLossFormProps) {
	const { dateFilter } = useDateFilter()
	const navigate = useNavigate()
	const [showRtpConfirm, setShowRtpConfirm] = useState(false)
	const [pendingPayload, setPendingPayload] = useState<Record<
		string,
		unknown
	> | null>(null)

	const form = useForm<ReturnLossFormValues>({
		resolver: zodResolver(returnLossFormSchema),
		defaultValues: {
			name: '',
			isActive: true,
			creditRatio: GAME_TYPES.reduce(
				(acc, type) => ({
					...acc,
					[type]: {
						creditRatio: undefined,
						maxCreditAmount: undefined,
					},
				}),
				{} as Record<
					GameType,
					{ creditRatio: number; maxCreditAmount: number }
				>,
			),
			minimumLossAmount: undefined,
			calculateInterval: undefined,
			hasExpiration: false,
			walletCondition: {
				minimumWithdrawCredit: undefined,
				maximumWithdrawCredit: undefined,
				turnoverRound: undefined,
				gameTypes: [],
				whitelistGameProviders: [],
				usePool: false,
				poolPresetConfigs: [],
				presetConfigs: [],
				pgHard: false,
				jokerHard: false,
				jiliHard: false,
				ppHard: false,
				nlcHard: false,
			},
			...defaultValues,
		},
	})

	const handleSubmit = (values: ReturnLossFormValues) => {
		const { hasExpiration, walletCondition, ...rest } = values

		const payload = {
			...rest,
			startDate: hasExpiration ? dateFilter.startDate : null,
			endDate: hasExpiration ? dateFilter.endDate : null,
			walletCondition: prepareWalletConditionForSubmit(walletCondition),
			// TODO: remove this after backend is updated
			turnoverRound: 1,
		}

		if (mode === 'create') {
			setPendingPayload(payload)
		} else {
			onSubmit(payload)
		}
	}

	return (
		<FormProvider {...form}>
			{mode === 'edit' && (
				<div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
					<TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
					<div className="text-amber-800 dark:text-amber-200">
						<p className="font-medium">
							การแก้ไขนี้จะมีผลกับแรงค์ที่ใช้คืนยอดเสียนี้อยู่เท่านั้น
						</p>
						<p className="mt-0.5">
							กรุณาตรวจสอบให้แน่ใจว่าการเปลี่ยนแปลงนี้ถูกต้องสำหรับทุกแรงค์ที่ใช้งานอยู่
						</p>
					</div>
				</div>
			)}
			<form
				onSubmit={(e) => {
					e.preventDefault()
					const result = form.handleSubmit((values) => {
						handleSubmit(values)
					}, showFormValidationToast)
					result(e)
				}}
				className="space-y-8"
			>
				{/* Basic Information and Time Period Section */}
				<div className="grid grid-cols-2 gap-4">
					{/* Basic Information Section */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ข้อมูลทั่วไป</h2>
						<div className="space-y-4">
							<NameField control={form.control} />
						</div>
					</div>

					{/* Time Period Section */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">ระยะเวลา</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="hasExpiration"
								render={({ field }) => (
									<FormItem className="space-y-3">
										<FormLabel>ระยะเวลาโปรโมชั่น</FormLabel>
										<FormControl>
											<RadioGroup
												onValueChange={(value) =>
													field.onChange(value === 'true')
												}
												defaultValue={field.value ? 'true' : 'false'}
												className="flex flex-col space-y-1"
											>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="false" />
													</FormControl>
													<FormLabel className="font-normal">
														ไม่มีกำหนด
													</FormLabel>
												</FormItem>
												<FormItem className="flex items-center space-x-3 space-y-0">
													<FormControl>
														<RadioGroupItem value="true" />
													</FormControl>
													<FormLabel className="font-normal">
														เลือกระยะเวลา
													</FormLabel>
												</FormItem>
											</RadioGroup>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							{form.watch('hasExpiration') && <DateRangePicker />}
						</div>
					</div>
				</div>

				{/* Return Loss Settings Section */}
				<div className="grid grid-cols-2 gap-4">
					{/* Loss Amount Settings */}
					<div className="space-y-6 rounded-lg border p-6">
						<h2 className="text-xl font-semibold">เงื่อนไขการคืนยอดเสีย</h2>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="minimumLossAmount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator />
											ยอดเสียขั้นต่ำ
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกยอดเสียขั้นต่ำ"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(
														e.target.value ? Number(e.target.value) : null,
													)
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="calculateInterval"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator />
											ระยะเวลาคำนวณ
										</FormLabel>
										<Select
											onValueChange={(value) => field.onChange(value)}
											defaultValue={field.value}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="เลือกระยะเวลาคำนวณ" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{CALCULATE_INTERVALS.map((interval) => (
													<SelectItem
														key={interval.value}
														value={interval.value}
													>
														{interval.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				{/* Game Type Credit Settings Section */}
				<div className="space-y-6 rounded-lg border p-6">
					<h2 className="text-xl font-semibold">
						การตั้งค่าเครดิตตามประเภทเกม
					</h2>
					<GameTypeCreditSettings control={form.control} name="creditRatio" />
				</div>

				{/* Wallet Condition Section */}
				<WalletConditionSection<ReturnLossFormValues>
					control={form.control}
					fieldPrefix="walletCondition"
					showGameSettings={true}
					showPoolPreset={true}
				/>

				{/* Action Buttons */}
				<div className="flex items-center gap-4 pb-8">
					<Button
						type="button"
						variant="default"
						className="w-full"
						onClick={() => {
							form.handleSubmit(() => {
								setShowRtpConfirm(true)
							}, showFormValidationToast)()
						}}
					>
						{mode === 'create' ? 'สร้างโปรโมชั่น' : 'แก้ไขโปรโมชั่น'}
					</Button>
					<Button
						type="button"
						variant="outline"
						className="w-full"
						onClick={() => navigate(-1)}
					>
						ยกเลิก
					</Button>
				</div>
			</form>
			<RtpConfirmDialog
				open={showRtpConfirm}
				onOpenChange={setShowRtpConfirm}
				onConfirm={() => {
					setShowRtpConfirm(false)
					form.handleSubmit(
						(values) => handleSubmit(values),
						showFormValidationToast,
					)()
				}}
				confirmLabel={mode === 'create' ? 'สร้างโปรโมชั่น' : 'แก้ไขโปรโมชั่น'}
			/>
			<AlertDialog
				open={pendingPayload !== null}
				onOpenChange={(open) => {
					if (!open) setPendingPayload(null)
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการสร้างคืนยอดเสีย</AlertDialogTitle>
						<AlertDialogDescription className="space-y-2">
							<span className="block">
								รายการคืนยอดเสียที่สร้างใหม่จะมีผลต่อผู้เล่นเมื่อถูกเชื่อมกับแรงค์ที่ต้องการแล้วเท่านั้น
							</span>
							<span className="block font-medium text-foreground">
								หลังจากสร้างแล้ว อย่าลืมไปที่หน้า <strong>จัดการแรงค์</strong>{' '}
								เพื่อเชื่อมรายการคืนยอดเสียนี้ให้กับแรงค์ที่ต้องการ
							</span>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setPendingPayload(null)}>
							กลับไปแก้ไข
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (pendingPayload) {
									onSubmit(pendingPayload)
									setPendingPayload(null)
								}
							}}
						>
							สร้างคืนยอดเสีย
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</FormProvider>
	)
}
